import throwWithoutBreaking from '@motivo/guanyin/src/utils/throwWithoutBreaking';
import config from './config';
import { clearAuthState } from './auth-utils';
import * as Sentry from '@sentry/react';

function addAuthBreadcrumb(message: string, data: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category: 'auth',
    level: 'info',
    message,
    data,
  });
}

function parseUrlForBreadcrumb(url: string) {
  try {
    const parsedUrl = new URL(url);
    return {
      origin: parsedUrl.origin,
      pathname: parsedUrl.pathname,
      searchKeys: [...parsedUrl.searchParams.keys()].sort(),
    };
  } catch {
    return {
      parseError: true,
    };
  }
}

export class ApiError extends Error {
  code: string | undefined;
  status: number;

  constructor(status: number, body: { code?: string; message?: string }) {
    super(body.message || `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
  }
}

const customFetch = async (input: RequestInfo, body?: BodyInit) => {
  const response = await fetch(input, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body,
  });
  if (!response.ok) {
    const responseText = await response.text();
    let errorBody: { code?: string; message?: string } = {};
    if (responseText) {
      try {
        errorBody = JSON.parse(responseText);
      } catch {
        errorBody = { message: responseText };
      }
    }
    throw new ApiError(response.status, errorBody);
  }
  if (response.status === 204) {
    return '';
  }
  const clonedResponse = response.clone();

  try {
    return await response.json();
  } catch (e) {
    const responseText = await clonedResponse.text();
    throwWithoutBreaking(e, { extra: { responseText } });
  }
  return undefined;
};

export const exchangeCode = async (code: string, isAdmin: boolean, state?: string) => {
  return customFetch(
    `${config.apiUrl}/auth/code`,
    JSON.stringify({
      code,
      origin: window.location.origin,
      isAdmin,
      state,
      ...(config.motivoProduct === 'astraea' && !isAdmin && { isMFE: true }),
    }),
  );
};

export const fetchAccessToken = async () => {
  return customFetch(
    `${config.apiUrl}/auth/accessToken`,
    JSON.stringify({
      origin: window.location.origin,
      ...(config.motivoProduct === 'zeus' && { isAdmin: true }),
      ...(config.motivoProduct === 'astraea' && { isMFE: true }),
    }),
  );
};

export const signIn = async ({
  userId,
  organizationAdminId,
  origin,
}: {
  userId?: number;
  organizationAdminId?: number;
  origin?: string;
}) => {
  const { authorizationUrl } = await customFetch(
    `${config.apiUrl}/auth/authorizationUrl`,
    JSON.stringify({
      origin: origin || window.location.origin,
      ...(config.motivoProduct === 'zeus' && { isAdmin: true }),
      ...(config.motivoProduct === 'astraea' && { isMFE: true }),
      ...(userId && { motivoUserId: userId }),
      ...(organizationAdminId && { organizationAdminId }),
    }),
  );

  addAuthBreadcrumb('sign_in_redirecting_to_workos', {
    motivoProduct: config.motivoProduct,
    currentPath: window.location.pathname,
    currentSearchKeys: [...new URLSearchParams(window.location.search).keys()].sort(),
    authorizationUrlMeta: parseUrlForBreadcrumb(authorizationUrl),
  });
  window.location.href = authorizationUrl;
};

export const signOut = async ({
  userId,
  organizationAdminId,
}: {
  userId?: string;
  organizationAdminId?: string;
}) => {
  const { logoutUrl } = await customFetch(
    `${config.apiUrl}/auth/logoutUrl`,
    JSON.stringify({
      ...((config.motivoProduct === 'zeus' || userId || organizationAdminId) && { isAdmin: true }),
      ...(config.motivoProduct === 'astraea' && { isMFE: true }),
    }),
  );
  clearAuthState();
  window.location.href = logoutUrl;
};

/** Uses plain fetch (not Apollo) so the errorLink can't intercept and trigger logout. */
export const isSessionValid = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${config.apiUrl}/auth/sessionStatus`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (!response.ok) return false;
    const data = await response.json();
    return data.valid === true;
  } catch {
    return false;
  }
};
